/**
 * useLibraryContent - the GM's library items of some Armoury kinds, read for an editor that copies from them (the
 * Team tab's Tactics, the Effects tab's Always on items and Solo modes; plan modes-rework): each item's current
 * version read once, cached by id and version, so a library change re-reads only what changed (the Armoury shelves'
 * pattern, `useArmouryShelves`).
 *
 * @param access - the GM's library; null for none (a player, an account that cannot host)
 * @param kinds - which kinds; a constant array, so the read is keyed on the library alone
 * @param wanted - read only while something shows them
 * @returns the items, newest version each, sorted by name; null without a library
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { readArmouryAsset, type LibraryAccess } from '../assetSession';
import type { ArmouryContent, ArmouryKind } from '../engine/assets/armouryKinds';
import type { AssetRow } from '../engine/assets/types';
import { diag, errorSummary } from '../diagnostics/log';

/** One library item as an editor offers it: which asset and version, its kind, and what it holds. */
export interface LibraryItem<K extends ArmouryKind> {
  asset: Pick<AssetRow, 'id' | 'version' | 'name'>;
  kind: K;
  content: ArmouryContent[K];
}

/** The kinds the Effects tab copies onto a sheet (a constant, so a read of them is keyed on the library alone). */
export const BUNDLE_KINDS = ['alwaysOn', 'soloMode'] as const;
/** One of them, as the Effects tab offers it. */
export type LibraryBundle = LibraryItem<(typeof BUNDLE_KINDS)[number]>;

const keyOf = (a: Pick<AssetRow, 'id' | 'version'>) => `${a.id}@${a.version}`;

/** Read what the cache lacks, all at once; drop what the library no longer holds. One that will not read is logged. */
async function readAhead<K extends ArmouryKind>(
  access: LibraryAccess,
  rows: AssetRow[],
  cache: Map<string, LibraryItem<K>>,
) {
  const wanted = new Set(rows.map(keyOf));
  for (const key of cache.keys()) if (!wanted.has(key)) cache.delete(key);
  await Promise.all(rows.filter((row) => !cache.has(keyOf(row))).map((row) => readOne(access, row, cache)));
}

async function readOne<K extends ArmouryKind>(
  access: LibraryAccess,
  row: AssetRow,
  cache: Map<string, LibraryItem<K>>,
) {
  try {
    const kind = row.kind as K;
    const content = await readArmouryAsset(access, row, kind);
    cache.set(keyOf(row), { asset: { id: row.id, version: row.version, name: row.name }, kind, content });
  } catch (err) {
    diag.error('assets', `the library item ${row.name} could not be read`, errorSummary(err));
  }
}

export default function useLibraryContent<K extends ArmouryKind>(
  access: LibraryAccess | null,
  kinds: readonly K[],
  wanted: boolean,
): LibraryItem<K>[] | null {
  const cache = useRef(new Map<string, LibraryItem<K>>());
  const [, setRead] = useState(0);
  // Saved at least once (version 0 is an asset just made, with nothing to read) and not in the trash.
  const rows = useMemo(
    () =>
      access && wanted
        ? access.lib.assets.filter((a) => kinds.includes(a.kind as K) && !a.deletedAt && a.version > 0)
        : [],
    [access, wanted, kinds],
  );
  // Re-read on what the library holds (its rows' ids and versions), not on the library object's identity.
  const signature = rows.map(keyOf).join('|');
  const now = useRef({ access, rows });
  now.current = { access, rows };
  useEffect(() => {
    const { access: at, rows: these } = now.current;
    if (!at) return;
    let gone = false;
    void readAhead(at, these, cache.current).then(() => !gone && setRead((n) => n + 1));
    return () => {
      gone = true;
    };
  }, [signature]);
  return access ? listed(rows, cache.current) : null;
}

/** What the cache holds of these rows, by name. */
const listed = <K extends ArmouryKind>(rows: readonly AssetRow[], cache: Map<string, LibraryItem<K>>) =>
  rows
    .map((r) => cache.get(keyOf(r)))
    .filter((t): t is LibraryItem<K> => t !== undefined)
    .sort((a, b) => a.asset.name.localeCompare(b.asset.name));
